/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { ITextField } from '@fluentui/react/lib/TextField';
import { LuisKeyCodes } from 'src/core/utils/keyCodes';
import { PatternDropdownType } from 'src/luis/verticals/patternInput/types';

/**
 * Handles the behavior of the braces in the following cases:
 * - Skips typing the closing '}' in case of it is already exists and the cursor was behind an existing '}'.
 * - Closes the opened '{' and setting the cursor to be in the middle between the braces pair.
 *
 * @param text The text to be handled if needed.
 * @param componentRef The reference to the text field component, used in setting the position of the cursor.
 * @param event The occurred event object that includes the pressed key code.
 * @param setSearch Callback to set the text with the new key to search with.
 * @param setDropDown Callback to set the drop down type to be displayed if needed.
 * @param setFocusOnMenu Callback to set focus whether it should be in the menu or not.
 * @returns The preprocessed string.
 */
export const preProcessText = (
    text: string,
    componentRef: ITextField,
    event: React.KeyboardEvent,
    setSearch: React.Dispatch<React.SetStateAction<string>>,
    setDropDown: React.Dispatch<React.SetStateAction<PatternDropdownType>>
): string => {
    const start = componentRef ? componentRef.selectionStart : 0;
    const currentChar = text.charAt(start);
    const previousChar = text.charAt(start - 1);

    /**
     * Skips typing in the `}` if the current key was
     * already `}`. An example:
     *
     * - "{example|}" <-- The '|' denotes the cursor position.
     * - Type `}`. The event is fired with a key of `}`
     * - The current character is where the cursor is at, which
     *   is the `}`.
     * - The event is skipped and the cursor is moved one step.
     * - "{example}|" <-- User can continue typing
     */
    if (event && event.key === LuisKeyCodes.CloseBraces && currentChar === LuisKeyCodes.CloseBraces) {
        event.preventDefault();
        setCursorPosition(componentRef, start + 1, setDropDown, setSearch);
    }

    /**
     * The open braces key was pressed. Override this event
     * by manually appending a `{` followed by a `}` in the text
     * to set.
     *
     * Splits the text at where the cursor was stopped
     * and adds the brackets between them. Ex:
     *
     * - "someExampleText"
     * - "someExample|Text" <-- The '|' is where the cursor stopped.
     * - `prefix` is now "someExample" and `suffix` is "Text"
     * - The new text is "someExample|{}Text"  <-- Cursor hasn't moved yet.
     * - The cursor is moved one index extra:
     * - "someExample{|}Text"
     */
    if (event && event.key === LuisKeyCodes.OpenBraces) {
        event.preventDefault();
        const prefix = text.slice(0, start);
        const suffix = text.slice(start);
        text = `${prefix}{}${suffix}`;
        setCursorPosition(componentRef, start + 1, setDropDown, setSearch);
    }

    /**
     * The backspace key was pressed while the selection was
     * between two braces "{|}". The two braces should be deleted
     * and the text truncated to accommodate for their removal. Ex:
     *
     * - "example{|}Text" <-- The '|' denotes the cursor position.
     * - Current character is `}`, and the previous character is `}`.
     * - Backspace is pressed.
     * - `prefix` is now "example". `suffix` is now "Text".
     * - The cursor is still at its original position, so
     *   it is at: "exampleT|ext", which is not what we want.
     * - Move the cursor one step backwards to move it to the
     *   desired position: "example|Text"
     */
    if (
        event &&
        event.key === LuisKeyCodes.Backspace &&
        previousChar === LuisKeyCodes.OpenBraces &&
        currentChar === LuisKeyCodes.CloseBraces
    ) {
        event.preventDefault();
        const prefix = text.slice(0, start - 1);
        const suffix = text.slice(start + 1);
        text = `${prefix}${suffix}`;
        setCursorPosition(componentRef, start - 1, setDropDown, setSearch);
        manageDropDown(text, setDropDown, componentRef.selectionEnd + 1, setSearch);
    }

    return text;
};

/**
 * Handles the key up event of the pattern input text field.
 *
 * @param text The text to be handled if needed.
 * @param componentRef The reference of the component, used in setting the position of the cursor.
 * @param setDropDown Callback to set the dropDown type to be displayed if needed.
 * @param setSearch Callback to set the text with the new key to search with.
 * @returns The post processed string.
 */
export const postProcessText = (
    text: string,
    componentRef: ITextField,
    setDropDown: React.Dispatch<React.SetStateAction<PatternDropdownType>>,
    setSearch: React.Dispatch<React.SetStateAction<string>>
): string => {
    manageDropDown(text, setDropDown, componentRef ? componentRef.selectionEnd : 0, setSearch);

    return text;
};

/**
 * Sets the cursor to given position.
 *
 * @param componentRef The reference of the component, used in setting the position of the cursor.
 * @param position The new cursor position.
 * @param setDropDown Callback to set the dropDown type to be displayed if needed.
 * @param setSearch Callback to set the text with the new key to search with.
 */
export const setCursorPosition = (
    componentRef: ITextField,
    position: number,
    setDropDown: React.Dispatch<React.SetStateAction<PatternDropdownType>>,
    setSearch: React.Dispatch<React.SetStateAction<string>>
): void => {
    /**
     * This setTimeout is necessary to delay setting the cursor
     * between the braces to make sure that the concat is
     * completely applied.
     */
    requestAnimationFrame(() => {
        componentRef.setSelectionStart(position);
        componentRef.setSelectionEnd(position);
        manageDropDown(componentRef.value, setDropDown, componentRef.selectionEnd, setSearch);
    });
};

/**
 * Is responsible for the state of the drop downs whether
 * it should be opened or not.
 *
 * @param text The text that is used to determine the
 * drop down state.
 * @param setDropDown Callback to set the drop down
 * type to be displayed if needed.
 * @param position A value that should be added to the cursor
 * position to act like that the initiated event is
 * already applied.
 * @param setSearch Callback to set the text with the
 * new key to search with.
 */
const manageDropDown = (
    text: string,
    setDropDown: React.Dispatch<React.SetStateAction<PatternDropdownType>>,
    position: number,
    setSearch: React.Dispatch<React.SetStateAction<string>>
): void => {
    if (containsMismatchedOpenBracket(text, position)) {
        const searchingText = text.slice(text.slice(0, position).lastIndexOf('{') + 1, position);

        setSearch(searchingText);
        setDropDown(searchingText.search(':') === -1 ? 'entities' : 'roles');
    } else {
        setDropDown('none');
    }
};

/**
 * Checks the given string if there is an opening bracket before
 * the given position with no mismatched closing bracket.
 *
 * @param text The text to be checked.
 * @param stopIndex The index to stop the search at.
 * @returns boolean indicates the existence of the open curly bracket.
 */
export const containsMismatchedOpenBracket = (text: string, stopIndex: number): boolean => {
    const stack = [];

    for (let i = 0; i < stopIndex; i++) {
        if (text.charAt(i) === LuisKeyCodes.OpenBraces) {
            stack.push('{');
        } else if (text.charAt(i) === LuisKeyCodes.CloseBraces) {
            if (stack.length !== 0) {
                stack.pop();
            }
        }
    }

    return stack.length > 0;
};
